import { produce } from 'immer';
import { entity } from 'simpler-state';

import { logConfig, MOCK } from '../../configDebug';
import entityReduxLogger from '../debug/helpers/entity-redux-logger';
import { CallStatus } from '../enums/index';
import { GWMarketDataSummaryBucket } from '../models/gateway/types';
import { OHLCV } from '../models/market-data/types';
import { EMPTY_CHART_DATA } from '../store/market-data/charting/constants';
import { prepareChartData } from '../store/market-data/charting/helpers';
import { ChartDataPayload, ChartDataPortion, ChartDataRange, ChartDataSet, LoadChartDataPayload, SmallChartData } from '../store/market-data/charting/types';
import { random } from '../util/collectionHelpers';
import { isNumber } from '../util/DataHelpers';
import { gatewaySummaryBucketToChartRange } from '../util/GatewayHelpers';
import { isEmpty } from '../util/typeGuards';
import { NullableNumber, NullableString } from '../util/types';

import CallsCache from './calls-cache/CallsCache';
import SmallChartDataCache from './SmallChartDataCache';

export type BigChartData = {
  symbol: NullableString | string
  callsCount1D: number
  /** The active symbol's portion - a mirror of `dataBySymbol[symbol]`, kept for existing readers. */
  dataByRange: ChartDataPortion
  /** Chart data retained per symbol, so revisiting one renders from memory instead of refetching. */
  dataBySymbol: Record<string, ChartDataPortion>
  status: CallStatus
}

const INITIAL_STATE = {
  symbol: null,
  callsCount1D: 0,
  dataByRange: {} as ChartDataPortion,
  dataBySymbol: {} as Record<string, ChartDataPortion>,
  status: CallStatus.INITIAL,
};
const BigChartCache = entity<BigChartData>(INITIAL_STATE, entityReduxLogger('BigChart', 'big-chart'));

type CacheRangeDataPayload = {
  state: BigChartData
  symbol: string
  range: ChartDataRange
  data: ChartDataSet | null
}

function getCachedSymbolData(state: BigChartData, symbol: string): ChartDataPortion {
  return state.dataBySymbol[symbol] ?? {} as ChartDataPortion;
}

/**
 * Single write path for chart data. Everything lands in the per-symbol cache first; `dataByRange`
 * only mirrors it while that symbol is the active one. Writing `dataByRange` directly would leave
 * the two out of step and lose the data on the next symbol switch.
 */
function cacheRangeData({ state, symbol, range, data }: CacheRangeDataPayload): void {
  state.dataBySymbol[symbol] = {
    ...getCachedSymbolData(state, symbol),
    [range]: data,
  };

  if (state.symbol === symbol) {
    state.dataByRange = state.dataBySymbol[symbol];
  }
}

/** Switches the chart to a symbol, serving its cached ranges if it has been opened before. */
export function setBigChartActiveSymbol(symbol: string) {
  BigChartCache.set(
    produce((state: BigChartData) => {
      state.symbol = symbol;
      state.dataByRange = getCachedSymbolData(state, symbol);
      state.status = isEmpty(state.dataByRange) ? CallStatus.INITIAL : CallStatus.READY;
    }),
  );
}

/** Used only by `loadChartData` reducer in market-data/index. */
export function updateBigChartCacheOnLoadChartData(
  actionPayload: LoadChartDataPayload,
  hasToMakeRequest: boolean,
  isPreCachedFromSmallChart: boolean,
  lineClose?: NullableNumber,
) {
  const { bucket, symbol, hasRangeChanged } = actionPayload;
  const range = gatewaySummaryBucketToChartRange(bucket) as ChartDataRange;

  BigChartCache.set(
    produce((state: BigChartData) => {
       
      const hasSymbolChanged = state.symbol !== symbol;
      const isInvalidBucket = bucket === GWMarketDataSummaryBucket.Invalid || !bucket;
      const isOneDayBucket = bucket === GWMarketDataSummaryBucket.OneDay;

      if (isInvalidBucket) {
        console.warn(`[market-data/index] updateBigChartCacheOnLoadChartData - invalid bucket '${bucket}'`);
        return;
      }

      if (isOneDayBucket) {
        if (hasSymbolChanged) {
          state.callsCount1D = 1;
          state.dataByRange = getCachedSymbolData(state, symbol);
        } else {
          state.callsCount1D++;
        }
      }

      state.status = isPreCachedFromSmallChart && !hasToMakeRequest ? CallStatus.READY : CallStatus.PENDING;
      state.symbol = symbol;

      const smallChartCache = SmallChartDataCache.get(symbol) as SmallChartData;

      switch (true) {
        case isPreCachedFromSmallChart: {
          // Add pre-cached chart data for 1D from small chart when available
          const fallbackLineClose = lineClose
            ?? smallChartCache?.ohlcvData?.[smallChartCache.ohlcvData.length - 1]?.close
            ?? null;
          cacheRangeData({
            state,
            symbol,
            range: '1D',
            data: prepareChartData(
              fallbackLineClose,
              smallChartCache.ohlcvData,
              '1D',
              { isAuthoritativeLineClose: lineClose != null },
            ),
          });
          break;
        }

        case hasRangeChanged && hasToMakeRequest:
          cacheRangeData({ state, symbol, range, data: null });
          break;

        default:
          break;
      }

      const isDev = process.env.NODE_ENV === 'development';
      // logConfig imported statically
      if (isDev && logConfig.chart) {
        const debugTitle = `[BigChartCache] State updated on request for ${bucket}`;
        const debugData = {
          state: { ...state }, // a copy is used to make state (a Proxy) printable
          isPreCachedFromSmallChart,
          isEmpty: isEmpty(smallChartCache.ohlcvData),
          smallChartCache,
          callsCount1D: state.callsCount1D,
        };
        console.info(debugTitle);
        console.debug(debugTitle, debugData);
      }
    }),
  );
}

/** Used only by Gateway response message MarketDataSummaryResponse or DataSummaryResponse. */
export function updateBigChartCacheOnLoadChartDataCompleted(
  newData: ChartDataPayload,
  traceId: string,
  lineClose?: NullableNumber,
) {
  CallsCache.processResponseByTraceId(traceId);
  BigChartCache.set(
    produce((state: BigChartData) => {
       
      const { data, bucket, symbol } = newData;
      const range = (isNumber(bucket) || !bucket ? null : bucket)?.substring(0, 2) as ChartDataRange;
      // A response for a symbol the user has already navigated away from is still worth caching,
      // but must not pull the visible chart back to it.
      if (state.symbol == null || state.symbol === symbol) {
        state.status = CallStatus.READY;
        state.symbol = symbol;
        state.dataByRange = getCachedSymbolData(state, symbol);
      }
      if (generateDataIfMockedMode(state, data, range, symbol)) return;

      const effectiveLineClose = lineClose ?? data?.[data.length - 1]?.close ?? null;
      const parsedData = prepareChartData(effectiveLineClose, data, range, {
        isAuthoritativeLineClose: lineClose != null,
      });

      cacheRangeData({ state, symbol, range, data: parsedData });

      const isDev = process.env.NODE_ENV === 'development';
      // logConfig imported statically
      if (isDev && logConfig.chart) {
         
        const debugTitle = `[BigChart] [Response] State updated from response for bucket: ${bucket}, range: ${range}`;
        const debugData = {
          newData,
          state: { ...state },
          callsCount1D: state.callsCount1D,
        };
        console.info(debugTitle);
        console.debug(debugTitle, debugData);
      }
       
    }),
  );
}

export function updateBigChartLineClose(lineClose: NullableNumber) {
  if (lineClose == null) return;

  BigChartCache.set(
    produce((state: BigChartData) => {
      const data = state.dataByRange?.['1D'];
      const symbol = state.symbol;
      if (data && !data.isValid && data.lineData?.length && symbol) {
        cacheRangeData({
          state,
          symbol,
          range: '1D',
          data: prepareChartData(
            lineClose,
            data.rawData,
            '1D',
            { isAuthoritativeLineClose: true },
          ),
        });
      }
    }),
  );
}

function generateDataIfMockedMode(state: BigChartData, data: OHLCV[], range: ChartDataRange, symbol: string) {
  if (MOCK.ENABLED) {
    // 1W range is `null` - to demonstrate the no data case
    const randomLineClose = random(22.07, 135, true);
    cacheRangeData({
      state,
      symbol,
      range,
      data: range === '1W'
        ? EMPTY_CHART_DATA(false)
        : prepareChartData(randomLineClose, data, range, { isAuthoritativeLineClose: true }),
    });

    return true;
  }

  return false;
}

export default {
  use: BigChartCache.use,
  get: BigChartCache.get,
  setActiveSymbol: setBigChartActiveSymbol,
  clear: () => BigChartCache.set(INITIAL_STATE),
};
